<template>
  <div
    v-if="thumbnailUrl && hasStatus && item.status === 'pending'"
    class="relative overflow-hidden rounded-2"
    :style="{ width: asPx(width), height: asPx(height) }"
  >
    <CProcessingOverlay
      :message="`${t('canvasUI.files.preparing')}...`"
      spin-size="small"
      :style="{ backgroundColor: themeVars.primary6_1 }"
    />
  </div>
  <CImage
    v-else-if="thumbnailUrl"
    :broken-image-size="`${iconSize}px`"
    class="rounded-2"
    :file-data="item"
    :height="height"
    :img-props="{ draggable: false }"
    lazy
    object-fit="cover"
    :preview-disabled="!preview"
    :preview-src="!preview ? undefined : getPreviewUrl(thumbnailUrl, pitcherSettings)"
    :src="(hasStatus && item.status === 'pending' ? '' : thumbnailUrl) || ''"
    :type="hasStatus && item.status === 'failed' ? 'error' : 'default'"
    :width="width"
    v-bind="$attrs"
  />
  <div
    v-else-if="fileThumbnailUrls && fileThumbnailUrls?.length > 0 && hasStatus && item.status === 'pending'"
    class="relative overflow-hidden rounded-2"
    :style="{ width: asPx(width), height: asPx(height) }"
  >
    <CProcessingOverlay
      :message="`${t('canvasUI.files.preparing')}...`"
      spin-size="small"
      :style="{ backgroundColor: themeVars.primary6_1 }"
    />
  </div>
  <div
    v-else-if="fileThumbnailUrls && fileThumbnailUrls?.length > 0"
    class="grid grid-cols-2 grid-rows-2 gap-2 h-full p-2 bg-primary6 rounded-2"
    v-bind="$attrs"
    :style="{ width: asPx(width), height: asPx(height) }"
  >
    <CImage
      v-for="single_thumbnail_url in fileThumbnailUrls"
      :key="single_thumbnail_url"
      :broken-image-size="`${iconSize}px`"
      class="rounded-2 w-full h-full object-cover"
      :file-data="item"
      :img-props="{ draggable: false }"
      lazy
      object-fit="cover"
      preview-disabled
      :src="(hasStatus && item.status === 'pending' ? '' : single_thumbnail_url) || ''"
      :type="hasStatus && item.status === 'failed' ? 'error' : 'default'"
      width="100%"
      v-bind="$attrs"
    />
  </div>
  <div
    v-else-if="hasStatus && item.status === 'pending'"
    class="rounded-2 flex items-center justify-center relative overflow-hidden"
    v-bind="$attrs"
    :style="{ minWidth: asPx(width), minHeight: asPx(height) }"
  >
    <CProcessingOverlay
      :message="`${t('canvasUI.files.preparing')}...`"
      spin-size="small"
      :style="{ backgroundColor: themeVars.primary6_1 }"
    />
  </div>
  <!-- DEFAULT THUMBNAILS: -->
  <div v-else class="rounded-2 overflow-hidden" v-bind="$attrs" :style="{ width: asPx(width), height: asPx(height) }">
    <img
      v-if="defaultThumbnailUrl"
      :alt="item.name || 'File thumbnail'"
      class="w-full h-full object-cover"
      :src="defaultThumbnailUrl"
      @error="onThumbnailError"
    />
    <div v-else class="bg-primary6 w-full h-full flex items-center justify-center">
      <CIcon :color="themeVars.primary3" fa-type="fas" :icon="icon" :size="iconSize" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import useThemeVars from '@lib/composables/useThemeVars'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CProcessingOverlay from '@lib/components/CProcessingOverlay/CProcessingOverlay.vue'
import { ViewCompactItemType, type RenderableFile } from '@lib/types/files'
import { getPreviewUrl } from '@lib/util/url'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import { getDefaultThumbnailUrl, onThumbnailError as handleThumbnailError } from '@lib/utils/defaultThumbnail.util'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    item: RenderableFile
    width?: number | string
    height?: number | string
    iconSize?: number
    preview?: boolean
  }>(),
  {
    width: 152,
    height: 100,
    iconSize: 32,
    preview: false,
  },
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const themeVars = useThemeVars()
const { t } = useI18n()

const hasStatus = computed(() => 'status' in props.item)

const icon = computed(() => {
  const item = props.item
  if (('folder' in item && item.folder) || ('type' in item && item.type === ViewCompactItemType.Folder)) {
    return 'folder'
  } else if ('page' in item && item.page) {
    return 'memo'
  } else {
    return 'file'
  }
})

const fileThumbnailUrls = computed(() => {
  if ('file_thumbnail_urls' in props.item) {
    return props.item.file_thumbnail_urls
  } else if ('folder' in props.item && props.item.folder?.file_thumbnail_urls) {
    return props.item.folder.file_thumbnail_urls
  }
  return undefined
})

const thumbnailUrl = computed(() => {
  if ('thumbnail_url' in props.item && props.item.thumbnail_url) {
    return props.item.thumbnail_url
  } else if ('folder' in props.item && props.item.folder?.thumbnail_url) {
    return props.item.folder.thumbnail_url
  }
  return undefined
})

const defaultThumbnailUrl = computed(() => {
  const item = props.item
  if (('folder' in item && item.folder) || ('type' in item && item.type === ViewCompactItemType.Folder)) {
    return undefined
  }
  return getDefaultThumbnailUrl(props.item)
})

const onThumbnailError = handleThumbnailError

function asPx(value: number | string) {
  return typeof value === 'number' ? `${value}px` : value
}
</script>
